/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_DATE_DISPLAY_MODE,
  getUserDateDisplayMode,
  getUserLocale,
  getUserTimezone,
  setUserDateDisplayMode,
  setUserLocale,
  setUserTimezone,
  type DateDisplayMode,
} from '../utils/dateFormat';

interface LocaleContextValue {
  locale: string;
  setLocale: (locale: string) => void;
  timezone: string;
  setTimezone: (timezone: string) => void;
  dateDisplayMode: DateDisplayMode;
  setDateDisplayMode: (mode: DateDisplayMode) => void;
  formatDate: (date: Date | string | null | undefined) => string;
  formatDateTime: (date: Date | string | null | undefined) => string;
  formatTime: (date: Date | string | null | undefined) => string;
  formatRelative: (date: Date | string | null | undefined) => string;
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string;
  formatCurrency: (value: number, currency?: string) => string;
}

const DEFAULT_LOCALE = 'en-US';

/**
 * Dispatched on `window` when user preferences change outside of React state
 * (e.g. the auth flow hydrating localStorage from the loaded profile). The
 * provider re-reads localStorage so dependent components update without a reload.
 */
export const PREFERENCES_CHANGED_EVENT = 'cloudincidents:preferences-changed';

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<string>(() => getUserLocale() || DEFAULT_LOCALE);
  const [timezone, setTimezoneState] = useState<string>(() => getUserTimezone());
  const [dateDisplayMode, setDateDisplayModeState] = useState<DateDisplayMode>(
    () => getUserDateDisplayMode() || DEFAULT_DATE_DISPLAY_MODE,
  );

  const setLocale = useCallback((newLocale: string) => {
    setLocaleState(newLocale);
    setUserLocale(newLocale);
  }, []);

  const setTimezone = useCallback((newTimezone: string) => {
    setTimezoneState(newTimezone);
    setUserTimezone(newTimezone);
  }, []);

  const setDateDisplayMode = useCallback((mode: DateDisplayMode) => {
    setDateDisplayModeState(mode);
    setUserDateDisplayMode(mode);
  }, []);

  // Re-hydrate state when preferences change in localStorage from outside React
  // (login/profile load, or another browser tab). Setting state inside an event
  // handler is safe and keeps dependent components reactive without a reload.
  useEffect(() => {
    const rehydrate = () => {
      setLocaleState(getUserLocale() || DEFAULT_LOCALE);
      setTimezoneState(getUserTimezone());
      setDateDisplayModeState(getUserDateDisplayMode() || DEFAULT_DATE_DISPLAY_MODE);
    };

    window.addEventListener(PREFERENCES_CHANGED_EVENT, rehydrate);
    window.addEventListener('storage', rehydrate);
    return () => {
      window.removeEventListener(PREFERENCES_CHANGED_EVENT, rehydrate);
      window.removeEventListener('storage', rehydrate);
    };
  }, []);

  const value = useMemo<LocaleContextValue>(() => {
    const parseDate = (date: Date | string | null | undefined): Date | null => {
      if (!date) return null;
      if (date instanceof Date) return Number.isNaN(date.getTime()) ? null : date;

      const str =
        date.endsWith('Z') || date.includes('+') || /T\d{2}:\d{2}.*[+-]\d{2}/.test(date)
          ? date
          : `${date}Z`;
      const parsed = new Date(str);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const formatDate = (date: Date | string | null | undefined) => {
      const d = parseDate(date);
      if (!d) return '—';
      return d.toLocaleDateString(locale, {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    return {
      locale,
      setLocale,
      timezone,
      setTimezone,
      dateDisplayMode,
      setDateDisplayMode,
      formatDate,
      formatDateTime: (date) => {
        const d = parseDate(date);
        if (!d) return '—';
        return d.toLocaleString(locale, {
          timeZone: timezone,
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
      formatTime: (date) => {
        const d = parseDate(date);
        if (!d) return '—';
        return d.toLocaleTimeString(locale, {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
      },
      formatRelative: (date) => {
        const d = parseDate(date);
        if (!d) return '—';

        const diffMs = Date.now() - d.getTime();
        const diffSec = Math.floor(diffMs / 1000);
        const diffMin = Math.floor(diffSec / 60);
        const diffHour = Math.floor(diffMin / 60);
        const diffDay = Math.floor(diffHour / 24);

        if (diffSec < 60) return 'just now';
        if (diffMin < 60) return `${diffMin}m ago`;
        if (diffHour < 24) return `${diffHour}h ago`;
        if (diffDay < 7) return `${diffDay}d ago`;
        return formatDate(d);
      },
      formatNumber: (value, options) => new Intl.NumberFormat(locale, options).format(value),
      formatCurrency: (value, currency = 'USD') =>
        new Intl.NumberFormat(locale, { style: 'currency', currency }).format(value),
    };
  }, [locale, timezone, dateDisplayMode, setLocale, setTimezone, setDateDisplayMode]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }

  return context;
}
